import moment from 'moment'
import { airportMatchesFlight } from './flightNervePure'
import { formatAirportDisplay } from '../../helpers/airportDisplay'

// ── Flight-number field status (RD-4073) ────────────────────────────────────
// One status per flight-number field, shared by the Chauffeur sections, the booking-details
// edit panels and the standalone VIP search. Before this, the Chauffeur side carried a single
// boolean ("itinerary match") that was set false for three different causes — no flight, wrong
// airport, wrong day — so all three rendered the same red message and nothing could tell them
// apart, least of all the submit button.
//
// Pure module: no API import, so anything can use it without booting the app (helpers.js
// imports ../../index).
export const FLIGHT_FIELD_STATUS = {
  // Empty field, or a field the user cleared. The flight number is optional, so this never
  // blocks.
  NONE: 'none',
  // A non-empty value that has not settled yet (still inside the typing pause) or whose
  // lookup is in flight. Blocks submit but shows nothing beyond the existing spinner, so a
  // user who types "AF55" and clicks Continue inside the pause cannot slip an unvalidated
  // number through.
  VALIDATING: 'validating',
  // Fails isValidFlightNumberFormat. No API call is made. Shown, but does NOT block (see
  // blocksProgress).
  INVALID_FORMAT: 'invalid_format',
  // Format is fine but the lookup found nothing usable — shown, but does NOT block (see
  // blocksProgress). Also covers a resolved flight whose
  // day differs from the booked day: flightapi.io keys /airline by departure date and answers
  // a wrong date and a wrong flight with the SAME 400 ("Either your date is wrong or airline
  // code is wrong"), so the only date mismatches we ever observe are artifacts of our own
  // fallback ladder — not something to assert to the user as a mismatch.
  NOT_VERIFIED: 'not_verified',
  // Chauffeur: flight resolved, but its airport is not the booked pick-up / drop-off airport.
  ITINERARY_MISMATCH: 'itinerary_mismatch',
  // Standalone VIP: flight resolved, but it does not land at / depart from the booked airport.
  AIRPORT_MISMATCH: 'airport_mismatch',
  VERIFIED: 'verified',
}

const S = FLIGHT_FIELD_STATUS

// The single predicate every submit is gated on.
//
// Product (Valeria, 2026-10-09): "Do not allow to proceed only if itinerary doesn't match".
// So INVALID_FORMAT and NOT_VERIFIED are shown but do NOT block — neither contradicts the
// itinerary, and the chauffeur is scheduled at the step-1 time the banner states (which is also
// what the not-verified banner tells the user: "You can still continue with your booking").
// VALIDATING still blocks: until the lookup answers, a mismatch cannot be ruled out.
//
// Chauffeur surfaces only (incl. add-on VIP). The standalone VIP search gates on its own flags
// (see useVIPMeetGreetSearch) and keeps blocking every error: a VIP arrival takes its greeter
// time from the verified flight and has no step-1 time to fall back on.
export const blocksProgress = status =>
  status === S.VALIDATING ||
  status === S.ITINERARY_MISMATCH ||
  status === S.AIRPORT_MISMATCH

export const isFlightErrorStatus = status =>
  status === S.INVALID_FORMAT ||
  status === S.NOT_VERIFIED ||
  status === S.ITINERARY_MISMATCH ||
  status === S.AIRPORT_MISMATCH

export const FLIGHT_FIELD_MESSAGES = {
  [S.INVALID_FORMAT]: 'Invalid flight number format',
  [S.NOT_VERIFIED]: 'Flight not verified',
  [S.ITINERARY_MISMATCH]: "This flight number doesn't match your itinerary",
}

// Figma 14491-70038. Amber and red differ in more than colour: amber messages are Medium with
// a 2px gap under the field, red is Regular with none — carried by the tone class in SCSS.
const PRESENTATION = {
  [S.VERIFIED]: {
    tone: 'verified',
    icon: '/images/icons/flight-status-verified.svg',
  },
  [S.INVALID_FORMAT]: {
    tone: 'warning',
    icon: '/images/icons/flight-status-warning.svg',
  },
  [S.NOT_VERIFIED]: {
    tone: 'warning',
    icon: '/images/icons/flight-status-warning.svg',
  },
  [S.ITINERARY_MISMATCH]: {
    tone: 'error',
    icon: '/images/icons/flight-status-error.svg',
  },
  [S.AIRPORT_MISMATCH]: {
    tone: 'error',
    icon: '/images/icons/flight-status-error.svg',
  },
}

// `message` overrides the default — the VIP airport-mismatch copy is directional and names the
// booked airport, so it is built by the caller. Returns null for statuses that render nothing.
export const statusPresentation = (status, message) => {
  const base = PRESENTATION[status]
  if (!base) return null
  return {
    ...base,
    message:
      status === S.VERIFIED
        ? null
        : message || FLIGHT_FIELD_MESSAGES[status] || null,
  }
}

// ── VIP airport mismatch copy ───────────────────────────────────────────────
export const vipAirportMismatchMessage = (direction, airportCode) => {
  const code = airportCode || 'the selected airport'
  return direction === 'departure'
    ? `This flight doesn't depart from ${code}`
    : `This flight doesn't land at ${code}`
}

// ── "10:15 on Tue, 13 Feb" ──────────────────────────────────────────────────
// The chauffeur's scheduled arrival as the user chose it on step 1 — never flight data, which
// by definition is absent in both "unavailable" banners.
//
// Step-1 values are wall-clock strings ("13/02/2026", "10:15"); stored booking dates follow the
// app's UTC-LABELLED wall-clock convention. So strings are parsed strictly and anything else is
// read through moment.utc. Never new Date(): parsing a naive value in the browser's zone is what
// shifted saved pick-up times by the viewer's UTC offset in production.
const DATE_FORMATS = [
  'DD/MM/YYYY',
  'YYYY-MM-DD',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DDTHH:mm:ss',
]

const readWallClock = value => {
  if (value == null || value === '') return null
  if (typeof value === 'string') {
    const strict = moment.utc(value, DATE_FORMATS, true)
    if (strict.isValid()) return strict
    // ISO only — a bare moment.utc(string) falls back to Date() parsing for anything else.
    const iso = moment.utc(value, moment.ISO_8601)
    return iso.isValid() ? iso : null
  }
  const parsed = moment.utc(value)
  return parsed.isValid() ? parsed : null
}

const readTime = value => {
  if (!value) return null
  if (typeof value === 'string' && /^\d{1,2}:\d{2}$/.test(value.trim())) {
    const [h, m] = value.trim().split(':')
    return { hours: Number(h), minutes: Number(m) }
  }
  const parsed = readWallClock(value)
  return parsed ? { hours: parsed.hours(), minutes: parsed.minutes() } : null
}

export const formatScheduledArrival = (date, time) => {
  const day = readWallClock(date)
  if (!day) return null
  const t = readTime(time)
  if (t) day.hours(t.hours).minutes(t.minutes)
  return `${day.format('HH:mm')} on ${day.format('ddd, D MMM')}`
}

// ── Airport labels for the itinerary-mismatch banner ────────────────────────
// Both airports read the same way (QA): the RD-3960 `{Airport name} ({IATA code})`, e.g.
// "Heathrow Airport (LHR)". FlightNerve names the airport without the word ("Heathrow",
// "LaGuardia", "Los Angeles International"), so it is added when missing; city + code is only
// the fallback when there is no name at all.
const AIRPORT_WORD = /\b(airport|airfield|aerodrome|airstrip|heliport|aeropuerto|aeroporto|aéroport|flughafen)\b/i

export const flightAirportLabel = airportInfo => {
  if (!airportInfo) return ''
  const shortName = String(airportInfo.name || '').trim()
  const name =
    shortName && !AIRPORT_WORD.test(shortName)
      ? `${shortName} Airport`
      : shortName
  const code = String(airportInfo.iata_faa || '').trim()
  if (name && code) {
    return name.includes(`(${code})`) ? name : `${name} (${code})`
  }
  if (name) return name
  return [airportInfo.city, code].filter(Boolean).join(' ')
}

// The booked airport follows the RD-3960 house standard for an airport shown as booking
// information — `{Airport name} ({IATA code})`, e.g. "London Heathrow Airport (LHR)" — through the
// shared formatter rather than a local split. A first-comma-segment split is exactly what RD-3960
// removed: Google lists Changi street-first, so it rendered "Airport Boulevard". The formatter also
// never doubles a code already in the name. `fallbackIata` (e.g. resolved from our airport cache
// when the Google place carries none) is offered as the explicit code.
//
// Some Google places are not comma-separated but dash-separated ("Dubai International Airport
// (DXB) - Airport Road - Dubai - United Arab Emirates"), so the formatter cannot find the name
// segment, returns the whole address and appends the code a second time. The airport is whatever
// ends at the first parenthesised code, so everything after it is the street address and is cut.
const FIRST_NAME_WITH_CODE = /^(.*?\(\s*[A-Z]{3}\s*\))/

export const bookedAirportLabel = (location, fallbackIata) => {
  const label =
    formatAirportDisplay(
      location,
      fallbackIata ? { iata_code: fallbackIata } : undefined
    ) ||
    fallbackIata ||
    ''
  const match = label.match(FIRST_NAME_WITH_CODE)
  return match ? match[1] : label
}

// ── Airport mismatch recovered from the route (RD-4073) ─────────────────────
// The API rejects a real flight that does not touch the booked airport (400
// `airport_mismatch`, see peekAirportMismatchRoute) and keeps nothing of it. The route the
// same lookup fetched still says where the flight really goes. Returns a payload shaped like
// a lookup result (`data.arrivalInfo` / `data.departureInfo`), flagged `fromRoute` because it
// carries no times — there is no flight card to show for it.
//
// Null — "not a clean airport mismatch", so the caller keeps "Flight not verified" — when the
// booked airport is unknown, or appears ANYWHERE on the route: the booked end, or any stop.
// A multi-leg number that does pass through the booked airport was rejected for some other
// reason, and calling that an airport mismatch would be wrong.
export const airportMismatchFromRoute = (route, bookedAirport, direction) => {
  if (!route || !route.departure || !route.arrival) return null
  if (!bookedAirport || (!bookedAirport.iata && !bookedAirport.fullAddress)) {
    return null
  }
  const toInfo = airport =>
    airport
      ? {
          iata_faa: airport.iata || '',
          city: airport.city || '',
          name: airport.name || '',
        }
      : null
  const bookedEnd = direction === 'departure' ? route.departure : route.arrival
  const candidates = [
    bookedEnd,
    ...(Array.isArray(route.stops) ? route.stops : []),
  ]
  const touchesBookedAirport = candidates.some(
    airport =>
      airport &&
      airportMatchesFlight(
        { iata: bookedAirport.iata, fullAddress: bookedAirport.fullAddress },
        airport.iata,
        toInfo(airport)
      )
  )
  if (touchesBookedAirport) return null
  return {
    data: {
      arrivalInfo: toInfo(route.arrival),
      departureInfo: toInfo(route.departure),
    },
    fromRoute: true,
  }
}
